import type { FC } from 'react';
import React, { useCallback } from 'react';
import Slider from 'react-slick';
import { Box } from '@withjoy/joykit';
import { useGetFilledPhotos } from '../../hooks/useGetFilledPhotos';
import { SliderContainer } from './PhotoSlider.styles';
import type { Photo, AlohaPhotoSliderProps } from './PhotoSlider.types';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { useSliderSettings } from './hooks/useSliderSettings';
import { PHOTO_COUNT_TO_SHOW, ASPECT_RATIO } from './PhotoSlider.constants';
import { OptimizedEventGuestSitePhoto } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto';

export const AlohaPhotoSlider: FC<AlohaPhotoSliderProps> = props => {
  const { photos, photoHeight, showPlaceholder } = props;

  const { filledPhotos } = useGetFilledPhotos(photos, PHOTO_COUNT_TO_SHOW, showPlaceholder && photos.length === 0);

  const [containerDimensions, containerRef] = useSizeObserver();
  const { settings, sliderRef } = useSliderSettings();

  const getPhotoComponent = useCallback(
    (photo: Photo) => (
      <Box
        key={photo.id}
        width="100%"
        height={photoHeight || '100%'}
      >
        <OptimizedEventGuestSitePhoto
          photoUrl={photo.photoUrl}
          cropInfo={photo.cropInfo ? { ...photo.cropInfo, unit: '%' } : null}
          width={Math.max(containerDimensions.width, containerDimensions.height)}
          height={Math.max(containerDimensions.width, containerDimensions.height)}
          containerType="square"
        />
      </Box>
    ),
    [containerDimensions.height, containerDimensions.width, photoHeight],
  );

  return (
    <SliderContainer
      ref={containerRef}
      width="100%"
      height="100%"
      position="relative"
    >
      <Slider
        ref={sliderRef}
        {...settings}
      >
        {filledPhotos.map((photo, index) => {
          return photo.photoUrl ? (
            getPhotoComponent(photo)
          ) : (
            <Box
              key={index}
              width="100%"
              height={photoHeight || '100%'}
              aspectRatio={{ _: ASPECT_RATIO.mobile, sm2: ASPECT_RATIO.desktop }}
              backgroundColor="lightgray"
            />
          );
        })}
      </Slider>
    </SliderContainer>
  );
};

AlohaPhotoSlider.displayName = 'AlohaPhotoSlider';
